import { useEffect } from "react";
import { useParams, Navigate } from "react-router-dom";
import LocalizedLink from "@/components/LocalizedLink";
import { ArrowLeft, ArrowRight } from "lucide-react";
import ArticleCover from "@/components/ArticleCover";
import ArticleBody from "@/components/ArticleBody";
import RelatedTools from "@/components/RelatedTools";
import SaveButton from "@/components/SaveButton";
import { useArticles } from "@/content/ContentProvider";
import { pickLang, useLanguage, langPrefix } from "@/i18n/LanguageContext";

const LABELS = {
  ar: {
    articles: "المقالات",
    crumbAria: "فتات الخبز",
    back: "العودة إلى المقالات",
    readAlso: "اقرأ أيضًا:",
    brand: "مجتمع الذكاء الاصطناعي السوري",
    defaultTitle: "مجتمع الذكاء الاصطناعي السوري | Syrian AI Society",
    defaultDescription:
      "مجتمع الذكاء الاصطناعي السوري — محتوى عربي مبسّط ودليل أدوات مجانية للطلاب والباحثين والمعلّمين.",
  },
  en: {
    articles: "Articles",
    crumbAria: "Breadcrumb",
    back: "Back to articles",
    readAlso: "Read also:",
    brand: "Syrian AI Society",
    defaultTitle: "Syrian AI Society | مجتمع الذكاء الاصطناعي السوري",
    defaultDescription:
      "Syrian AI Society — simplified Arabic-first AI content and a free tools directory for students, researchers, and educators.",
  },
};

/* ------------------------------------------------------------------ */
/* SEO helpers — keep DOM meta tags in sync with the active article.  */
/* ------------------------------------------------------------------ */
function upsertMeta(attr, name, content) {
  if (!content) return null;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return el;
}

function removeMetaIfOwned(el) {
  if (el && el.parentNode) el.parentNode.removeChild(el);
}

export default function ArticleDetail() {
  const { slug } = useParams();
  const { lang } = useLanguage();
  const l = LABELS[lang];
  const { getArticleBySlug, getArticleCategory, ARTICLES } = useArticles();
  const article = getArticleBySlug(slug);

  useEffect(() => {
    if (!article) return undefined;

    const title =
      pickLang(article.metaTitle, lang) ||
      `${pickLang(article.title, lang)} | ${l.brand}`;
    const description =
      pickLang(article.metaDescription, lang) ||
      pickLang(article.excerpt, lang) ||
      l.defaultDescription;
    const keywordsArr = (article.keywords && article.keywords[lang]) || [];
    const keywords = Array.isArray(keywordsArr)
      ? keywordsArr.join(", ")
      : String(keywordsArr || "");

    const previousTitle = document.title;
    document.title = title;

    // Ensure core SEO tags exist and reflect the current article.
    const descEl = upsertMeta("name", "description", description);
    const kwEl = keywords ? upsertMeta("name", "keywords", keywords) : null;
    // Open Graph (social share preview) — cheap, no-op otherwise.
    const ogTitleEl = upsertMeta("property", "og:title", title);
    const ogDescEl = upsertMeta("property", "og:description", description);
    const ogTypeEl = upsertMeta("property", "og:type", "article");

    return () => {
      document.title = previousTitle || l.defaultTitle;
      // Reset description to the site default so other pages don't inherit
      // an article-specific description after navigating away.
      if (descEl) descEl.setAttribute("content", l.defaultDescription);
      // Tags we only added for the article page can be safely removed.
      removeMetaIfOwned(kwEl);
      removeMetaIfOwned(ogTitleEl);
      removeMetaIfOwned(ogDescEl);
      removeMetaIfOwned(ogTypeEl);
    };
  }, [article, lang, l.brand, l.defaultTitle, l.defaultDescription]);

  if (!article) return <Navigate to={`${langPrefix(lang)}/articles` || "/articles"} replace />;

  // Related-article picker: prefer same category, then fall back to any other
  // article so that the footer link is never empty when more than one article
  // exists. We surface up to 2 candidates (currently only the first is shown
  // in the footer, but keeping the array makes it trivial to expand later).
  const related = (() => {
    const others = ARTICLES.filter((a) => a.slug !== article.slug);
    const sameCategory = others.filter((a) => a.category === article.category);
    const rest = others.filter((a) => a.category !== article.category);
    return [...sameCategory, ...rest].slice(0, 2);
  })();
  const cat = getArticleCategory(article.category);
  const catLabel = pickLang(cat?.label, lang);
  const BackIcon = lang === "ar" ? ArrowLeft : ArrowRight;

  return (
    <article>
      <header className="sas-section-sm border-b border-[color:var(--sas-line)]" data-testid="article-header">
        <div className="sas-container">
          <nav className="text-xs text-[color:var(--sas-ink-2)] mb-6" aria-label={l.crumbAria}>
            <LocalizedLink to="/articles" className="hover:text-[color:var(--sas-accent)]">{l.articles}</LocalizedLink>
            <span className="mx-2" aria-hidden>/</span>
            <span>{catLabel}</span>
          </nav>
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-5 flex-wrap">
              <span className="sas-chip">{catLabel}</span>
              <span className="text-xs text-[color:var(--sas-ink-2)]">{pickLang(article.dateLabel, lang)}</span>
              {article.readingTime && (
                <>
                  <span aria-hidden className="text-[color:var(--sas-ink-2)]">·</span>
                  <span className="text-xs text-[color:var(--sas-ink-2)]">{pickLang(article.readingTime, lang)}</span>
                </>
              )}
            </div>
            <div className="flex items-start justify-between gap-4">
              <h1 className="sas-h1 flex-1">{pickLang(article.title, lang)}</h1>
              <div className="shrink-0 mt-2">
                <SaveButton
                  itemType="article"
                  itemSlug={article.slug}
                  itemId={article.id}
                  variant="inline"
                  testId={`article-save-detail-${article.slug}`}
                  stopPropagation={false}
                />
              </div>
            </div>
            {article.summary && <p className="sas-lead mt-5">{pickLang(article.summary, lang)}</p>}
          </div>
        </div>
      </header>

      <section className="sas-section-sm" data-testid="article-cover">
        <div className="sas-container">
          <div className="max-w-4xl mx-auto">
            <ArticleCover
              variant={article.coverVariant}
              imageUrl={article.coverUrl || ""}
              alt={pickLang(article.title, lang)}
            />
          </div>
        </div>
      </section>

      <section className="pb-16" data-testid="article-body-section">
        <div className="sas-container">
          <div className="max-w-2xl mx-auto">
            <ArticleBody blocks={article.body} />
            <RelatedTools toolSlugs={article.relatedTools} />
          </div>
        </div>
      </section>

      <section className="sas-section-sm border-t border-[color:var(--sas-line)]" data-testid="article-footer">
        <div className="sas-container">
          <div className="max-w-3xl mx-auto flex flex-wrap items-center justify-between gap-6">
            <LocalizedLink to="/articles" className="sas-btn-link">
              <BackIcon size={16} /> {l.back}
            </LocalizedLink>
            {related.length > 0 && (
              <div className="text-sm text-[color:var(--sas-ink-2)]">
                <span className="me-2">{l.readAlso}</span>
                <LocalizedLink to={`/articles/${related[0].slug}`} className="text-[color:var(--sas-ink)] hover:text-[color:var(--sas-accent)] font-medium">
                  {pickLang(related[0].title, lang)}
                </LocalizedLink>
              </div>
            )}
          </div>
        </div>
      </section>
    </article>
  );
}
